import {StyleSheet, css} from 'aphrodite';
import * as d3 from 'd3';
import React, {useEffect, useState} from 'react';

import {MaybeRenderWithSignedURL} from 'external-files/SignedExtFileURLProxy';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as net from 'util/Net';

import {ScratchFileContent} from './ScratchFileTypes';

export const DISPLAY_ROW_LIMIT = 1000;

interface ScratchFileCSVProps {
  scratchFile: ScratchFileContent;
}

function CSVFileContent({signedURL}: {signedURL: string}): JSX.Element {
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    net
      .getPromise(signedURL)
      .then((content: string) => {
        setCsvContent(content);
      })
      .catch(() => {
        setErrorMessage('Failed to load CSV file. Please contact Benchling support if the problem persists.');
      });
  }, [signedURL]);

  if (errorMessage) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />;
  }

  if (csvContent == null) {
    return <CenteredSpinner />;
  }

  const csvRows = d3.csvParseRows(csvContent);

  if (!csvRows || csvRows.length === 0) {
    return <InlineBanner bannerType={InlineBannerType.INFO} text="The CSV file is empty." />;
  }

  const headerRow = csvRows[0];
  const allDataRows = csvRows.slice(1);
  const totalRows = allDataRows.length;
  const displayedRows = allDataRows.slice(0, DISPLAY_ROW_LIMIT);
  const hasMoreRows = totalRows > DISPLAY_ROW_LIMIT;

  return (
    <div className={css(styles.tableWrapper)}>
      {hasMoreRows && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Showing the first ${DISPLAY_ROW_LIMIT} rows out of ${totalRows}`}
          supportingInfo="Download to view the complete data."
        />
      )}
      <Table
        calculateInView={true}
        className={css(styles.table)}
        emptyTableText="No data"
        fitContent={true}
        frozenHeader={true}
        getRowKey={(rowIndex: number) => rowIndex}
        isCompact={true}
        numRows={displayedRows.length}
        overflowVisible={true}
        showBorder={false}
      >
        {headerRow.map((header, columnIndex) => (
          <Column
            key={columnIndex}
            header={header}
            renderCell={(rowIndex: number) => (
              <Cell className={css(styles.cell)}>{displayedRows[rowIndex]?.[columnIndex] || ''}</Cell>
            )}
          />
        ))}
      </Table>
    </div>
  );
}

export default function ScratchFileCSV(props: ScratchFileCSVProps): JSX.Element {
  return (
    <MaybeRenderWithSignedURL
      externalFileId={props.scratchFile.externalFileId}
      url={props.scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredSpinner />;
        } else if (signedURL == null) {
          return (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="Error loading CSV file. Please contact Benchling support if the problem persists."
            />
          );
        } else {
          return <CSVFileContent signedURL={signedURL} />;
        }
      }}
      owningEntityApiId={props.scratchFile.id}
    />
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  table: {
    borderRadius: 0,
    margin: 0,
  },
  cell: {
    ...FONT_PROXIMA_13,
    height: 36,
  },
});
